// Employees: generation, skill, wages, loyalty, attendance, training transfers.
import { MASTERY_XP, STAFF, TASK_IDS, XP, type TaskId } from '../config/constants';
import { zeroTasks, type Employee, type SaveState, type StaffStation } from './GameState';

export const MOUSE_NAMES = [
  'Pip', 'Nibbles', 'Tansy', 'Bramble', 'Clover', 'Fennel', 'Dusty', 'Marlow', 'Sorrel', 'Wren',
  'Juniper', 'Basil', 'Hazel', 'Pepper', 'Rye', 'Sage', 'Thimble', 'Waffle', 'Cricket', 'Moss',
  'Poppy', 'Quill', 'Rowan', 'Tumble', 'Whisker', 'Barley', 'Cinder', 'Dapple',
];

export const STATION_TASKS: Record<StaffStation, TaskId[]> = {
  barn: ['feed', 'milk'],
  vat: ['pasteurize', 'curdle', 'culture', 'rennet', 'cut', 'stretch', 'brine', 'bag'],
  cellar: ['cellar'],
};

export const STATION_LABEL: Record<StaffStation, string> = {
  barn: 'Barn (feeds and milks Bessie)',
  vat: 'Creamery (turns milk into cheese)',
  cellar: 'Cellar (washes rinds, tends the dials)',
};

const STAT_KEYS = ['intelligence', 'workEthic', 'loyalty', 'hygiene'] as const;

export function totalXp(e: Employee, task: TaskId): number {
  return (e.hiredXp[task] ?? 0) + (e.trainedXp[task] ?? 0);
}

export function skillLevel(e: Employee, task: TaskId): number {
  return Math.floor(Math.sqrt(totalXp(e, task) / XP.LEVEL_DIVISOR));
}

/** 0..1, where 1 is mastery */
export function skillFactor(e: Employee, task: TaskId): number {
  return Math.min(1, totalXp(e, task) / MASTERY_XP);
}

export function isMaster(e: Employee, task: TaskId): boolean {
  return totalXp(e, task) >= MASTERY_XP;
}

export function sumXp(e: Employee): number {
  return TASK_IDS.reduce((n, t) => n + totalXp(e, t), 0);
}

/** How fast the employee learns and how rarely they slip: 0.6 at intelligence 1, 1.5 at 10 */
export function intelligenceFactor(e: Employee): number {
  return 0.5 + 0.1 * e.stats.intelligence;
}

/** Batches per day at the vat, milkings covered at the barn */
export function workUnits(e: Employee): number {
  return 1 + Math.floor(e.stats.workEthic / 4);
}

/** The wage an employee's experience commands. XP earned under your roof is cheaper. */
export function expectedWage(e: Employee): number {
  const hired = TASK_IDS.reduce((n, t) => n + (e.hiredXp[t] ?? 0), 0);
  const trained = TASK_IDS.reduce((n, t) => n + (e.trainedXp[t] ?? 0), 0);
  const base = STAFF.BASE_WAGE + hired * STAFF.WAGE_PER_HIRED_XP + trained * STAFF.WAGE_PER_TRAINED_XP;
  return Math.round(base * (e.trainerFor ? STAFF.TRAINER_WAGE_MULT : 1) * 100) / 100;
}

/** 0 when paid fairly, rising toward 1 as pay lags what experience commands. */
export function payMismatch(e: Employee): number {
  const expected = expectedWage(e);
  if (expected <= 0) return 0;
  return Math.max(0, (expected - e.wage) / expected);
}

export function attendanceChance(e: Employee): number {
  const c = STAFF.ATTEND_BASE + STAFF.ATTEND_PER_LOYALTY * e.stats.loyalty - STAFF.ATTEND_MISMATCH_PENALTY * payMismatch(e);
  return Math.min(1, Math.max(STAFF.ATTEND_MIN, c));
}

/** Daily chance of walking out. Loyalty and tenure (XP built here) hold people; low pay pushes them. */
export function quitChance(e: Employee): number {
  const mismatch = payMismatch(e);
  if (mismatch <= STAFF.QUIT_CHECK_MISMATCH) return 0;
  const trainedShare = TASK_IDS.reduce((n, t) => n + (e.trainedXp[t] ?? 0), 0) / Math.max(1, sumXp(e));
  const push = Math.max(0, mismatch - 0.05 * e.stats.loyalty);
  return Math.min(0.9, push * STAFF.QUIT_RATE * (1 - 0.5 * trainedShare));
}

export function signingFee(e: Employee): number {
  return Math.round(e.wage * STAFF.SIGNING_FEE_DAYS * 100) / 100;
}

export function statLabel(v: number): string {
  if (v >= 9) return 'superb';
  if (v >= 7) return 'good';
  if (v >= 5) return 'fair';
  if (v >= 3) return 'poor';
  return 'dismal';
}

export function generateCandidate(s: SaveState, rng: () => number = Math.random): Employee {
  const used = new Set(s.staff.employees.concat(s.staff.candidates).map((e) => e.name));
  const pool = MOUSE_NAMES.filter((n) => !used.has(n));
  const name = (pool.length ? pool : MOUSE_NAMES)[Math.floor(rng() * (pool.length ? pool.length : MOUSE_NAMES.length))];
  const stats = { intelligence: 1, workEthic: 1, loyalty: 1, hygiene: 1 };
  for (const k of STAT_KEYS) stats[k] = 1 + Math.floor(rng() * 10);
  // Experience profile: a few strong skills, the rest low
  const hiredXp = zeroTasks();
  const strength = rng();
  for (const t of TASK_IDS) {
    if (t === 'sell') continue;
    const roll = rng();
    const share = roll < 0.35 ? rng() : rng() * 0.25;
    hiredXp[t] = Math.round(share * strength * STAFF.HIRED_XP_MAX_PER_TASK);
  }
  const e: Employee = {
    id: `emp_${s.staff.nextId++}`,
    name,
    stats,
    hiredXp,
    trainedXp: zeroTasks(),
    wage: 0,
    trainerFor: null,
    station: null,
    hiredDay: 0,
    presentToday: true,
    daysWorked: 0,
    daysMissed: 0,
    jobStatus: 'idle',
    jobProgress: 0,
    jobLabel: '',
    blockedReason: null,
  };
  e.wage = expectedWage(e);
  return e;
}

export function refreshCandidates(s: SaveState, rng: () => number = Math.random): void {
  s.staff.candidates = [];
  for (let i = 0; i < STAFF.CANDIDATES; i++) s.staff.candidates.push(generateCandidate(s, rng));
  s.staff.candidatesDay = s.day;
}

export function candidatesStale(s: SaveState): boolean {
  return s.staff.candidates.length === 0 || s.day - s.staff.candidatesDay >= STAFF.CANDIDATE_REFRESH_DAYS;
}

export function canPromote(s: SaveState, e: Employee, task: TaskId): string | null {
  if (e.trainerFor) return `${e.name} already trains ${e.trainerFor}.`;
  if (!isMaster(e, task)) return 'Needs mastery in that process.';
  if (s.staff.employees.some((o) => o.trainerFor === task)) return 'There is already a trainer for that process.';
  return null;
}

export interface EmployeeStageResult {
  quality: number;
  yieldFactor: number;
  mistakes: TaskId[];
}

/**
 * Quality an employee reaches on a batch: the milk's quality, minus a share of
 * STAGE_PENALTY per stage scaled by how far they are from mastery, minus slips,
 * plus or minus hygiene.
 */
export function employeeBatch(e: Employee, baseQuality: number, stages: TaskId[], rng: () => number = Math.random, accuracyBonus = 0, applyHygiene = true): EmployeeStageResult {
  let quality = baseQuality;
  let yieldFactor = 1;
  const mistakes: TaskId[] = [];
  const iq = intelligenceFactor(e);
  for (const t of stages) {
    const gap = 1 - Math.min(1, skillFactor(e, t) + accuracyBonus);
    quality -= STAFF.STAGE_PENALTY * gap * (0.5 + rng());
    const slipChance = (STAFF.MISTAKE_BASE * gap) / iq;
    if (rng() < slipChance) {
      mistakes.push(t);
      if (t === 'cut' || t === 'rennet') yieldFactor -= STAFF.MISTAKE_PENALTY / 2;
      else quality -= STAFF.MISTAKE_PENALTY;
    }
  }
  if (applyHygiene) quality += (e.stats.hygiene - 5) * STAFF.HYGIENE_QUALITY_PER_POINT;
  return { quality: Math.min(5, Math.max(1, quality)), yieldFactor: Math.max(0.4, yieldFactor), mistakes };
}

export function grantXp(e: Employee, task: TaskId, amount: number): void {
  e.trainedXp[task] = Math.round(((e.trainedXp[task] ?? 0) + amount * intelligenceFactor(e)) * 10) / 10;
}

export interface TrainingReport {
  fromBank: { name: string; task: TaskId; amount: number }[];
  fromTrainer: { name: string; task: TaskId; amount: number }[];
}

/**
 * Day-end training. The player's banked XP flows to assigned employees at 2% a day per
 * employee once the player has mastered the process. Trainers pass 1% of their XP a day
 * to every junior in their process.
 */
export function applyTraining(s: SaveState, playerMastered: (task: TaskId) => boolean): TrainingReport {
  const report: TrainingReport = { fromBank: [], fromTrainer: [] };
  for (const e of s.staff.employees) {
    if (!e.station || !e.presentToday) continue;
    for (const t of STATION_TASKS[e.station]) {
      const bank = s.xp.bank[t] ?? 0;
      if (bank > 0 && playerMastered(t)) {
        const amount = Math.round(bank * XP.BANK_TRANSFER_PER_DAY * 10) / 10;
        if (amount > 0) {
          s.xp.bank[t] = Math.max(0, Math.round((bank - amount) * 10) / 10);
          grantXp(e, t, amount);
          report.fromBank.push({ name: e.name, task: t, amount });
        }
      }
    }
  }
  for (const trainer of s.staff.employees) {
    if (!trainer.trainerFor || !trainer.presentToday) continue;
    const t = trainer.trainerFor;
    for (const junior of s.staff.employees) {
      if (junior === trainer || !junior.presentToday || totalXp(junior, t) >= totalXp(trainer, t)) continue;
      const amount = Math.round(totalXp(trainer, t) * XP.TRAINER_TRANSFER_PER_DAY * 10) / 10;
      if (amount > 0) {
        grantXp(junior, t, amount);
        report.fromTrainer.push({ name: junior.name, task: t, amount });
      }
    }
  }
  return report;
}
